import React, { useEffect, useMemo, useRef, useState } from "react";
import { Redirect, useLocation } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { LoadingSpinner } from "../Components/LoadingSpinner";
import { client } from "../Clients";
import { bypassAuth } from "./auth0Config";

type AccessGateProps = {
  audience?: string;
  children: React.ReactNode;
};

export function AccessGate({ audience, children }: AccessGateProps): React.ReactElement {
  const { isAuthenticated, isLoading, getAccessTokenSilently, loginWithRedirect, error, logout, user } = useAuth0();
  if (bypassAuth) {
    return <>{children}</>;
  }
  const location = useLocation();
  const [hasAccess, setHasAccess] = useState<boolean | null>(null);
  const reauthStarted = useRef(false);
  const syncedRef = useRef(false);

  const returnTo = useMemo(
    () => `${location.pathname}${location.search}${location.hash}`,
    [location.pathname, location.search, location.hash]
  );

  useEffect(() => {
    let isMounted = true;
    if (!isAuthenticated) {
      setHasAccess(null);
      return;
    }

    (async () => {
      try {
        // Acquire a token to confirm the session is live (and trigger re-auth on expiry). Every
        // authenticated user is admitted — the server grants the Internal access role to every
        // authenticated principal; what they can see/do is governed by their assigned roles, which
        // the dynamic sidebar + route guards enforce. The root email gets full Admin access.
        await getAccessTokenSilently(audience ? { authorizationParams: { audience } } : undefined);
        if (isMounted) {
          setHasAccess(true);
        }
      } catch (err: any) {
        const errCode = err?.error ?? err?.code;
        const errMessage = err?.error_description ?? err?.message ?? "";
        const requiresLogin =
          ["login_required", "consent_required", "interaction_required", "invalid_grant", "missing_refresh_token"].includes(
            String(errCode)
          ) ||
          String(errMessage).toLowerCase().includes("login_required") ||
          String(errMessage).toLowerCase().includes("consent_required") ||
          String(errMessage).toLowerCase().includes("interaction_required") ||
          String(errMessage).toLowerCase().includes("missing_refresh_token") ||
          String(errMessage).toLowerCase().includes("invalid_grant") ||
          String(errMessage).toLowerCase().includes("expired");

        if (requiresLogin && !reauthStarted.current) {
          reauthStarted.current = true;
          setHasAccess(null);
          loginWithRedirect({
            appState: { returnTo },
            authorizationParams: { prompt: "login" }
          });
          return;
        }

        if (isMounted) {
          setHasAccess(false);
        }
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [isAuthenticated, getAccessTokenSilently, audience, loginWithRedirect, returnTo]);

  // Sync user profile to backend after access is confirmed
  useEffect(() => {
    if (hasAccess && user && !syncedRef.current) {
      syncedRef.current = true;
      const email = user.email;
      const displayName = user.name || user.nickname || user.email;
      if (email) {
        client.account.apiAccountSyncPost({ email, displayName }).catch(() => {
          // Ignore sync errors — user can still use the app
        });
      }
    }
  }, [hasAccess, user]);

  if (isLoading || (isAuthenticated && hasAccess === null)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <Redirect
        to={{
          pathname: "/",
          state: { returnTo },
        }}
      />
    );
  }

  if (!hasAccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-8 shadow-xl">
          <div className="text-xs uppercase tracking-[0.3em] text-slate-500">
            APA Admin
          </div>
          <h1 className="mt-2 text-2xl font-semibold text-slate-800">Access denied</h1>
          <p className="mt-3 text-sm text-slate-500">
            Your account is not authorized for the admin console. Please contact an
            administrator.
          </p>
          {error ? (
            <div className="mt-4 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
              {error.message}
            </div>
          ) : null}
          <button
            type="button"
            className="mt-6 w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-100"
            onClick={() =>
              logout({
                logoutParams: { returnTo: window.location.origin },
              })
            }
          >
            Logout
          </button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
